import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from 'firebase/auth';
import { auth } from '../firebase';
import { usersApi } from '../services/api';
// Note: ensureUserDocument removed - user creation now happens via invite acceptance

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  createAccountWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Whether the signed-in identity proves itself with a password. */
  signsInWithPassword: boolean;
  deleteAccount: (password?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
    // Note: User document is NOT created here anymore.
    // For new users, user creation happens during invite acceptance.
    // For existing users (login), the backend verifies they exist.
  };

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const createAccountWithEmail = async (email: string, password: string) => {
    await createUserWithEmailAndPassword(auth, email, password);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const signsInWithPassword = !!user?.providerData.some((p) => p.providerId === 'password');

  /**
   * Delete the account, then the identity that signs into it.
   *
   * The identity provider lets only a fresh sign-in delete itself, so the
   * person signs in again first: once that has happened the identity can
   * always follow the data, and a person is never left with a sign-in to an
   * account that is gone. The backend holds no right to delete identities.
   */
  const deleteAccount = async (password?: string) => {
    const current = auth.currentUser;
    if (!current) throw new Error('You are not signed in');
    try {
      if (current.providerData.some((p) => p.providerId === 'password')) {
        if (!password || !current.email) throw new Error('Enter your password to confirm');
        await reauthenticateWithCredential(current, EmailAuthProvider.credential(current.email, password));
      } else {
        await reauthenticateWithPopup(current, new GoogleAuthProvider());
      }
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        throw new Error('That password is not right');
      }
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        throw new Error('Sign in again to confirm it is you');
      }
      throw e;
    }
    await usersApi.deleteMe();
    await deleteUser(current);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        signInWithGoogle,
        signInWithEmail,
        createAccountWithEmail,
        resetPassword,
        logout,
        signsInWithPassword,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
